import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import CodeBlock from '../components/CodeBlock';
import { FlaskConical, Timer } from 'lucide-react';

const exerciseCode = `def troubleshoot_with_reasoning(problem_description, run_command):
    """
    Task: Implement reasoning-based network troubleshooter
    
    Test case: "Users in VLAN 30 cannot access internet,
                but VLAN 20 works fine."
    """
    reasoning_steps = []
    
    # Step 1: Parse problem
    reasoning_steps.append({
        'step': 1,
        'thought': 'VLAN 30 broken, VLAN 20 works. Same gateway.',
        'hypothesis': 'Problem is isolated to VLAN 30'
    })
    
    # Step 2: Check NAT translations
    result = run_command("show ip nat translations")
    # Expected: Only VLAN 20 entries present
    
    # Step 3: Check firewall/ACL
    result = run_command("show access-list")
    # Expected: ACL permits VLAN 20, denies others
    
    # TODO: Students complete additional steps...
    
    return {
        'root_cause': 'NAT not configured for VLAN 30',
        'confidence': 0.95,
        'solution': 'Add VLAN 30 to NAT ACL'
    }`;

const HandsOnSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Build a reasoning chain for network troubleshooting"
      >
        Hands-On Exercise
      </SlideTitle>
      
      <SlideContent>
        <div className="flex items-center gap-4 mb-6">
          <span className="tech-badge">
            <FlaskConical className="w-3 h-3 mr-1" />
            Exercise
          </span>
          <span className="tech-badge secondary">
            <Timer className="w-3 h-3 mr-1" />
            10 minutes
          </span>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Task description */}
          <div>
            <div className="slide-card mb-4 border-primary/30">
              <h3 className="font-heading font-semibold text-foreground mb-3">Scenario</h3>
              <p className="text-foreground italic">
                "Users in VLAN 30 cannot access the internet, but VLAN 20 works fine. 
                Both VLANs route through the same gateway router."
              </p>
            </div>
            
            <div className="slide-card">
              <h3 className="font-heading font-semibold text-foreground mb-3">Your Solution Should</h3>
              <ol className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-xs font-mono text-primary">1</div>
                  <span className="text-muted-foreground">Parse the problem</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-xs font-mono text-primary">2</div>
                  <span className="text-muted-foreground">Generate hypotheses</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-xs font-mono text-primary">3</div>
                  <span className="text-muted-foreground">Test systematically</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-xs font-mono text-primary">4</div>
                  <span className="text-muted-foreground">Identify root cause</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-xs font-mono text-primary">5</div>
                  <span className="text-muted-foreground">Propose solution</span>
                </li>
              </ol>
            </div>
            
            {/* Expected solution */}
            <div className="mt-4 p-4 rounded-xl bg-accent/10 border border-accent/30">
              <h4 className="font-heading font-semibold text-foreground mb-2">Expected Output</h4>
              <div className="text-sm space-y-1">
                <p className="text-muted-foreground">
                  <span className="text-accent">Root Cause:</span> NAT not configured for VLAN 30 subnet
                </p>
                <p className="text-muted-foreground">
                  <span className="text-accent">Evidence:</span> NAT translations show VLAN 20 only; ACL blocks VLAN 30
                </p>
                <p className="text-muted-foreground">
                  <span className="text-accent">Solution:</span> Add VLAN 30 to NAT pool and update ACL
                </p>
              </div>
            </div>
          </div>
          
          {/* Code */}
          <div>
            <CodeBlock 
              code={exerciseCode}
              title="exercise.py"
            />
          </div>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default HandsOnSlide;
